/**
 * Pricing Tab Component
 * Displays and manages room pricing rules
 */
import { useState } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Plus, IndianRupee, Tag, Edit, Trash2, 
  MoreVertical, CalendarDays, Sun, X 
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import PricingDialog from './dialogs/PricingDialog';
import SeasonalPricingDialog from './dialogs/SeasonalPricingDialog';
import WeekendPricingDialog from './dialogs/WeekendPricingDialog';
import { initialPricingFormData } from './constants';

export default function PricingTab({
  pricingRules,
  roomTypes,
  bedTypes,
  amenities,
  hotelRoomTypes,
  canManageRooms,
  loadData
}) {
  // Dialog states
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [pricingFormData, setPricingFormData] = useState(initialPricingFormData);
  
  // Seasonal/Weekend dialog states
  const [seasonalDialogOpen, setSeasonalDialogOpen] = useState(false);
  const [weekendDialogOpen, setWeekendDialogOpen] = useState(false);
  const [selectedPricingRule, setSelectedPricingRule] = useState(null);
  const [seasonalFormData, setSeasonalFormData] = useState({ seasonal_prices: [] });
  const [weekendFormData, setWeekendFormData] = useState({ weekend_price: '', weekend_enabled: false });

  const activeBedTypes = bedTypes.filter(b => b.is_active);
  const activeAmenities = amenities.filter(a => a.is_active);

  const getRoomTypeName = (roomTypeId) => {
    const rt = roomTypes.find(r => r.id === roomTypeId);
    return rt?.name || 'Unknown';
  };

  const resetPricingForm = () => {
    setEditingPricing(null);
    setPricingFormData(initialPricingFormData);
  };

  const openNewPricingDialog = () => {
    resetPricingForm();
    setPricingDialogOpen(true);
  };

  const handleEditPricing = (pricing) => {
    setEditingPricing(pricing);
    const bedTypeIds = pricing.bed_type_ids || (pricing.bed_type_id ? [pricing.bed_type_id] : []);
    setPricingFormData({
      name: pricing.name || '',
      room_type_id: pricing.room_type_id || '',
      bed_type_ids: bedTypeIds,
      amenity_ids: pricing.amenity_ids || [],
      base_price: pricing.base_price?.toString() || '',
      breakfast_price: pricing.breakfast_price?.toString() || '',
      lunch_price: pricing.lunch_price?.toString() || '',
      dinner_price: pricing.dinner_price?.toString() || '',
      is_active: pricing.is_active !== false
    });
    setPricingDialogOpen(true);
  };

  const handlePricingSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        name: pricingFormData.name,
        room_type_id: pricingFormData.room_type_id || null,
        bed_type_ids: pricingFormData.bed_type_ids || [],
        amenity_ids: pricingFormData.amenity_ids || [],
        base_price: parseFloat(pricingFormData.base_price) || 0,
        breakfast_price: parseFloat(pricingFormData.breakfast_price) || 0,
        lunch_price: parseFloat(pricingFormData.lunch_price) || 0,
        dinner_price: parseFloat(pricingFormData.dinner_price) || 0,
        is_active: pricingFormData.is_active
      };

      if (editingPricing) {
        await api.put(`/room-pricing/${editingPricing.id}`, data);
        toast.success('Pricing rule updated');
      } else {
        await api.post('/room-pricing', data);
        toast.success('Pricing rule created');
      }
      
      setPricingDialogOpen(false);
      resetPricingForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save pricing'));
    }
  };

  const handleDeletePricing = async (pricing) => {
    try {
      await api.delete(`/room-pricing/${pricing.id}`);
      toast.success('Pricing rule deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete pricing');
    }
  };

  // Seasonal pricing handlers
  const openSeasonalDialog = (pricing) => {
    setSelectedPricingRule(pricing);
    const existingPrices = pricing.seasonal_prices || [];
    const prices = existingPrices.length > 0 ? existingPrices : 
      (pricing.start_date && pricing.end_date ? [{ 
        id: 'legacy', 
        start_date: pricing.start_date, 
        end_date: pricing.end_date, 
        price: pricing.base_price 
      }] : []);
    setSeasonalFormData({ seasonal_prices: prices });
    setSeasonalDialogOpen(true);
  };

  const handleSeasonalSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPricingRule) return;
    
    for (const entry of seasonalFormData.seasonal_prices) {
      if (entry.start_date && entry.end_date && entry.start_date > entry.end_date) {
        toast.error('Start date must be before or equal to end date');
        return;
      }
      if (!entry.start_date || !entry.end_date || !entry.price) {
        toast.error('All fields are required for each seasonal entry');
        return;
      }
    }
    
    try {
      await api.put(`/room-pricing/${selectedPricingRule.id}`, {
        seasonal_prices: seasonalFormData.seasonal_prices.map(p => ({
          id: p.id.startsWith('new-') ? undefined : p.id,
          start_date: p.start_date,
          end_date: p.end_date,
          price: parseFloat(p.price)
        })),
        start_date: null,
        end_date: null
      });
      toast.success('Seasonal pricing updated');
      setSeasonalDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update seasonal pricing'));
    }
  };

  const clearSeasonalPricing = async (pricing) => {
    try {
      await api.put(`/room-pricing/${pricing.id}`, {
        start_date: null,
        end_date: null,
        seasonal_prices: []
      });
      toast.success('Seasonal pricing cleared');
      loadData();
    } catch (error) {
      toast.error('Failed to clear seasonal pricing');
    }
  };

  // Weekend pricing handlers
  const openWeekendDialog = (pricing) => {
    setSelectedPricingRule(pricing);
    setWeekendFormData({
      weekend_price: pricing.weekend_price?.toString() || '',
      weekend_enabled: pricing.weekend_enabled || false
    });
    setWeekendDialogOpen(true);
  };

  const handleWeekendSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPricingRule) return;
    
    try {
      await api.put(`/room-pricing/${selectedPricingRule.id}`, {
        weekend_price: weekendFormData.weekend_enabled ? (parseFloat(weekendFormData.weekend_price) || null) : null,
        weekend_enabled: weekendFormData.weekend_enabled
      });
      toast.success('Weekend pricing updated');
      setWeekendDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update weekend pricing'));
    }
  };

  const clearWeekendPricing = async (pricing) => {
    try {
      await api.put(`/room-pricing/${pricing.id}`, {
        weekend_price: null,
        weekend_enabled: false
      });
      toast.success('Weekend pricing cleared');
      loadData();
    } catch (error) {
      toast.error('Failed to clear weekend pricing');
    }
  };

  return (
    <>
      {canManageRooms && (
        <div className="mb-6">
          <Button onClick={openNewPricingDialog} className="bg-gradient-orange hover:opacity-90 text-white" data-testid="add-pricing-button">
            <Plus className="h-4 w-4 mr-2" />
            Add Pricing Rule
          </Button>
        </div>
      )}

      <Card className="border-orange-100">
        <CardHeader>
          <CardTitle className="text-gray-800 flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-orange-500" />
            Room Pricing Table
          </CardTitle>
          <p className="text-sm text-gray-500">Configure prices based on room type, bed type, amenities, and meal plans</p>
        </CardHeader>
        <CardContent>
          {pricingRules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="h-12 w-12 mx-auto text-orange-300 mb-4" />
              <p>No pricing rules defined yet</p>
              <p className="text-sm text-gray-400 mt-2">Create pricing rules to set room rates with amenities and meal plans</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Room Type</TableHead>
                  <TableHead>Bed Types</TableHead>
                  <TableHead>Base Price</TableHead>
                  <TableHead>Seasonal</TableHead>
                  <TableHead>Weekend</TableHead>
                  <TableHead>Meals</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricingRules.map((pricing) => {
                  const hasMeals = (pricing.breakfast_price > 0) || (pricing.lunch_price > 0) || (pricing.dinner_price > 0);
                  const bedTypesList = pricing.bed_type_ids || (pricing.bed_type_id ? [pricing.bed_type_id] : []);
                  const bedTypeNames = bedTypesList.map(id => {
                    const bt = bedTypes.find(b => b.id === id);
                    return bt ? bt.name : '';
                  }).filter(Boolean);
                  
                  return (
                    <TableRow key={pricing.id}>
                      <TableCell className="font-medium">{pricing.name || '-'}</TableCell>
                      <TableCell>{getRoomTypeName(pricing.room_type_id)}</TableCell>
                      <TableCell>
                        {bedTypeNames.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {bedTypeNames.map((name, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{name}</Badge>
                            ))}
                          </div>
                        ) : <span className="text-xs text-gray-400">Any</span>}
                      </TableCell>
                      <TableCell className="font-semibold text-orange-600">Rs{pricing.base_price || 0}</TableCell>
                      <TableCell>
                        {(pricing.seasonal_prices && pricing.seasonal_prices.length > 0) ? (
                          <div className="flex items-center gap-1">
                            <span 
                              className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded cursor-pointer" 
                              onClick={() => openSeasonalDialog(pricing)}
                            >
                              {pricing.seasonal_prices.length} period{pricing.seasonal_prices.length > 1 ? 's' : ''}
                            </span>
                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => clearSeasonalPricing(pricing)}>
                              <X className="h-3 w-3 text-gray-400" />
                            </Button>
                          </div>
                        ) : (pricing.start_date && pricing.end_date) ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                              {pricing.start_date} to {pricing.end_date}
                            </span>
                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => clearSeasonalPricing(pricing)}>
                              <X className="h-3 w-3 text-gray-400" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Not set</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {pricing.weekend_enabled && pricing.weekend_price ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                              Rs{pricing.weekend_price}
                            </span>
                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => clearWeekendPricing(pricing)}>
                              <X className="h-3 w-3 text-gray-400" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Not set</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {hasMeals ? (
                          <div className="text-xs">
                            {pricing.breakfast_price > 0 && <span className="block">B: Rs{pricing.breakfast_price}</span>}
                            {pricing.lunch_price > 0 && <span className="block">L: Rs{pricing.lunch_price}</span>}
                            {pricing.dinner_price > 0 && <span className="block">D: Rs{pricing.dinner_price}</span>}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={pricing.is_active ? 'default' : 'outline'} className={pricing.is_active ? 'bg-green-500' : ''}>
                          {pricing.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditPricing(pricing)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Rule
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openSeasonalDialog(pricing)}>
                              <CalendarDays className="h-4 w-4 mr-2" />
                              {pricing.start_date ? 'Edit Seasonal Pricing' : 'Add Seasonal Pricing'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openWeekendDialog(pricing)}>
                              <Sun className="h-4 w-4 mr-2" />
                              {pricing.weekend_enabled ? 'Edit Weekend Pricing' : 'Add Weekend Pricing'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDeletePricing(pricing)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Rule
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pricing Dialog */}
      <PricingDialog
        open={pricingDialogOpen}
        onOpenChange={(open) => { setPricingDialogOpen(open); if (!open) resetPricingForm(); }}
        editingPricing={editingPricing}
        formData={pricingFormData}
        setFormData={setPricingFormData}
        onSubmit={handlePricingSubmit}
        hotelRoomTypes={hotelRoomTypes}
        activeBedTypes={activeBedTypes}
        activeAmenities={activeAmenities}
      />

      {/* Seasonal Pricing Dialog */}
      <SeasonalPricingDialog
        open={seasonalDialogOpen}
        onOpenChange={setSeasonalDialogOpen}
        selectedPricingRule={selectedPricingRule}
        formData={seasonalFormData}
        setFormData={setSeasonalFormData}
        onSubmit={handleSeasonalSubmit}
      />

      {/* Weekend Pricing Dialog */}
      <WeekendPricingDialog
        open={weekendDialogOpen}
        onOpenChange={setWeekendDialogOpen}
        selectedPricingRule={selectedPricingRule}
        formData={weekendFormData}
        setFormData={setWeekendFormData}
        onSubmit={handleWeekendSubmit}
      />
    </>
  );
}
